import { useSnackbar } from 'contexts/snackbar';
import { useWebsocket } from 'contexts/websocket';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import apiService from 'services/api';
import batchService from 'services/batch';
import quizService from 'services/quiz';
import { filterImportableQuizSubmissions, getQuizSubmissionProgress } from 'utils/quizSubmissions';
import { v4 as uuid } from 'uuid';

const mergeBatch = (batch, update) => ({
    ...batch,
    ...update,
    submission_count: Math.max(batch.submission_count || 0, update.submission_count || 0),
    is_finished: Boolean(batch.is_finished || update.is_finished),
    is_failed: Boolean(batch.is_failed || update.is_failed),
});

const useQuizSubmissionImport = ({ quiz, auth }) => {
    const history = useHistory();
    const { message } = useSnackbar();
    const { onMessage, setPingPayload } = useWebsocket();
    const { t } = useTranslation('quiz');
    const [batches, setBatches] = useState([]);
    const [loadingBatches, setLoadingBatches] = useState(true);
    const [reviewed, setReviewed] = useState(0);
    const [reviewable, setReviewable] = useState(0);
    const [findingSubmissions, setFindingSubmissions] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [foundSubmissions, setFoundSubmissions] = useState([]);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const requestId = useRef(0);
    const busy = useRef(false);
    const progress = useMemo(() => getQuizSubmissionProgress(batches), [batches]);
    const isProcessing = submitting || progress.processing.length > 0;
    const canImport =
        auth?.role === 'instructor' && Boolean(quiz?.api_assignment_id) && !loadingBatches && !isProcessing;

    useEffect(() => {
        const currentRequest = ++requestId.current;
        busy.current = false;
        setBatches([]);
        setReviewed(0);
        setReviewable(0);
        setFoundSubmissions([]);
        setConfirmationOpen(false);
        setPreviewOpen(false);
        setFindingSubmissions(false);
        setSubmitting(false);
        setLoadingBatches(Boolean(quiz?.id));
        if (!quiz?.id) return;

        quizService
            .listQuizBatch(quiz.id)
            .then((result) => {
                if (requestId.current !== currentRequest) return;
                if (!Array.isArray(result)) throw new Error('Invalid quiz batch list');
                setBatches(result);
            })
            .catch(() => {
                if (requestId.current === currentRequest) {
                    message.error(
                        t('submissions.status-error', 'Submission status could not be loaded. Please try again.')
                    );
                }
            })
            .finally(() => {
                if (requestId.current === currentRequest) setLoadingBatches(false);
            });
        return () => {
            requestId.current += 1;
            busy.current = false;
        };
    }, [auth?.course_id, auth?.owner_account_id, auth?.role, quiz?.id, message, t]);

    useEffect(() => {
        if (!quiz?.id) return;
        let active = true;
        quizService
            .listQuizSubmissions(quiz.id)
            .then((submissions) => {
                if (!active || !Array.isArray(submissions)) return;
                setReviewed(
                    submissions.filter(
                        ({ grading_status, grading }) =>
                            grading_status === 'confirmed' || (!grading_status && Boolean(grading))
                    ).length
                );
                setReviewable(
                    submissions.filter(
                        ({ grading_status, grading, ai, is_failed }) =>
                            (grading_status === 'in_progress' || !grading) && (ai || is_failed)
                    ).length
                );
            })
            .catch(() => {
                // Batch progress remains available if review status cannot be refreshed.
            });
        return () => {
            active = false;
        };
    }, [auth?.course_id, auth?.owner_account_id, auth?.role, quiz?.id, progress.processed]);

    useEffect(
        () => setPingPayload(() => ({ batch_ids: progress.processing.map(({ id }) => id) })),
        [progress.processing, setPingPayload]
    );

    useEffect(
        () =>
            onMessage((event) => {
                if (event.type !== 'pong' || !event.batches) return;
                setBatches((current) =>
                    current.map((batch) => {
                        const update = event.batches[batch.id];
                        if (!update || (update.quiz_id && update.quiz_id !== quiz?.id)) return batch;
                        return mergeBatch(batch, {
                            submission_count: update.submission_count,
                            is_finished: update.is_finished,
                            is_failed: update.is_failed,
                        });
                    })
                );
            }),
        [onMessage, quiz?.id]
    );

    const closeDialogs = () => {
        if (busy.current) return;
        setConfirmationOpen(false);
        setPreviewOpen(false);
        setFoundSubmissions([]);
    };

    const importSubmissions = async () => {
        if (!canImport || busy.current || !quiz?.course_id || auth?.course_id !== quiz.course_id) return;
        const currentRequest = ++requestId.current;
        busy.current = true;
        setFindingSubmissions(true);
        try {
            const result = await apiService.getQuizSubmissions({
                access_token: localStorage.getItem('access_token'),
                course_id: quiz.api_data?.course_id || auth.course_id,
                quiz_id: quiz.api_assignment_id,
            });
            if (currentRequest !== requestId.current) return;
            if (
                !Array.isArray(result) ||
                result.some(
                    (submission) =>
                        !submission ||
                        submission.api?.id == null ||
                        submission.api?.user?.id == null ||
                        !Array.isArray(submission.responses)
                )
            ) {
                throw new Error('Invalid LMS quiz submissions');
            }
            setFoundSubmissions(filterImportableQuizSubmissions(result, batches));
            setConfirmationOpen(true);
        } catch {
            if (currentRequest === requestId.current) {
                message.error(t('submissions.import-error', 'Quiz submissions could not be loaded. Please try again.'));
            }
        } finally {
            if (currentRequest === requestId.current) {
                busy.current = false;
                setFindingSubmissions(false);
            }
        }
    };

    const confirmImport = () => {
        setConfirmationOpen(false);
        setPreviewOpen(foundSubmissions.length > 0);
    };

    const startProcessing = async () => {
        if (!canImport || !foundSubmissions.length || busy.current || !previewOpen) return;
        const currentRequest = ++requestId.current;
        busy.current = true;
        setSubmitting(true);
        const timestamp = Date.now();
        const batch = {
            id: uuid(),
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.owner_account_id || auth.id,
            course_id: quiz.course_id,
            quiz_id: quiz.id,
            type: 'quiz',
            submissions: foundSubmissions,
            is_finished: false,
        };
        setBatches((current) => [...current, batch]);
        try {
            const createdBatch = await batchService.createSubmissions(batch);
            if (currentRequest !== requestId.current) return;
            setBatches((current) =>
                current.map((item) => (item.id === batch.id ? mergeBatch(item, createdBatch) : item))
            );
            setPreviewOpen(false);
            setFoundSubmissions([]);
        } catch {
            if (currentRequest === requestId.current) {
                setBatches((current) => current.filter((item) => item.id !== batch.id));
                message.error(
                    t('submissions.processing-error', 'Quiz processing could not be started. Please try again.')
                );
            }
        } finally {
            if (currentRequest === requestId.current) {
                busy.current = false;
                setSubmitting(false);
            }
        }
    };

    const startReviewing = () => {
        if (quiz?.id && !isProcessing) history.push(`/quizzes/${quiz.id}/grading`);
    };

    return {
        ...progress,
        reviewed,
        pendingReview: auth?.role === 'ta' ? reviewable : Math.max(progress.processed - reviewed, 0),
        canImport,
        loadingBatches,
        findingSubmissions,
        submitting,
        isProcessing,
        foundSubmissions,
        confirmationOpen,
        previewOpen,
        onImportSubmissions: importSubmissions,
        onConfirmImport: confirmImport,
        onCloseImportDialogs: closeDialogs,
        onStartProcessing: startProcessing,
        onStartReviewing: startReviewing,
    };
};

export default useQuizSubmissionImport;
